import { useState } from "react";
import { useSession } from "@/auth/session";
import { changeMyPassword } from "@/lib/api";

/**
 * Pantalla bloqueante para quien entra con una clave temporal.
 *
 * Al contratar un plan la clave se envia por correo y se muestra en pantalla,
 * asi que la conoce mas de una persona: no corresponde dejar operar con ella.
 * No hay forma de saltarla salvo cerrar sesion.
 */
export function ForcePasswordChange() {
  const { session, markPasswordChanged, login, logout } = useSession();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suficienteLarga = nueva.length >= 8;
  const coinciden = nueva.length > 0 && nueva === repetir;
  const distintaDeLaActual = nueva.length > 0 && nueva !== actual;
  const puedeGuardar = Boolean(actual) && suficienteLarga && coinciden && distintaDeLaActual && !guardando;

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!session || !puedeGuardar) return;
    setGuardando(true);
    setError(null);
    try {
      await changeMyPassword(session, { currentPassword: actual, newPassword: nueva });
      // El cambio revoca los refresh tokens, asi que la sesion actual moriria al
      // expirar el access token. Reingresamos con la clave nueva para dejarla sana.
      try {
        await login({ email: session.email, password: nueva });
      } catch {
        // Si el reingreso falla igual hay que soltar el bloqueo: la clave ya cambio.
        markPasswordChanged();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cambiar la clave");
      setGuardando(false);
    }
  }

  const input = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-5">
            <i className="ri-shield-keyhole-line text-xl text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Cambia tu clave para continuar</h1>
          <p className="text-sm text-gray-500 mt-2">
            Estás usando la clave temporal que te enviamos al contratar. Por seguridad,
            defínela de nuevo antes de entrar al sistema.
          </p>

          <form onSubmit={guardar} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Clave actual</label>
              <input type="password" value={actual} onChange={(e) => setActual(e.target.value)}
                className={input} autoComplete="current-password" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Clave nueva</label>
              <input type="password" value={nueva} onChange={(e) => setNueva(e.target.value)}
                className={input} autoComplete="new-password" />
              <p className={`text-xs mt-1 ${nueva.length === 0 ? "text-gray-400" : suficienteLarga ? "text-emerald-600" : "text-amber-600"}`}>
                Al menos 8 caracteres
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Repite la clave nueva</label>
              <input type="password" value={repetir} onChange={(e) => setRepetir(e.target.value)}
                className={input} autoComplete="new-password" />
              {repetir.length > 0 && !coinciden && (
                <p className="text-xs mt-1 text-red-600">Las claves no coinciden</p>
              )}
            </div>

            {nueva.length > 0 && !distintaDeLaActual && (
              <p className="text-xs text-amber-600">La clave nueva debe ser distinta de la temporal.</p>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>
            )}

            <button type="submit" disabled={!puedeGuardar}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg text-sm disabled:opacity-50">
              {guardando ? "Guardando..." : "Cambiar clave y entrar"}
            </button>
          </form>
        </div>

        <button onClick={logout} className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700">
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
